import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import styles from './Video.module.scss';
import { IVideo } from '../../types';

/** Matches the play glyph the rest of the hardware-first surfaces use. */
const PlayIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M8 5.2v13.6a1 1 0 0 0 1.53.85l10.5-6.8a1 1 0 0 0 0-1.7L9.53 4.35A1 1 0 0 0 8 5.2Z" />
  </svg>
);

/**
 * Plays only while on screen. A muted loop that runs the whole time the page is open burns
 * battery for something nobody is looking at, and a video parked in a carousel slide is off to
 * the side rather than gone, so geometry alone would keep it running.
 */
function usePlayWhileVisible(ref: React.RefObject<HTMLVideoElement>, enabled: boolean) {
  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled || typeof IntersectionObserver === 'undefined') {
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // a lazily-loaded video only starts fetching once it is worth watching; an eager one
          // is already buffered by now and just needs starting
          if (el.preload === 'none') {
            el.preload = 'auto';
          }
          void el.play().catch(() => {});
        } else if (!el.paused) {
          el.pause();
        }
      },
      { threshold: 0.4 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, enabled]);
}

export const Video = ({
  video,
  className,
  fill,
}: {
  video: IVideo;
  className?: string;
  /** Fills the parent box rather than keeping the video's own aspect inside it. */
  fill?: boolean;
}) => {
  const ref = useRef<HTMLVideoElement>(null);
  const [tapped, setTapped] = useState(false);
  // the scrim sits over a loop that is already running - it is an invitation to take control of
  // the video, not a gate in front of it
  const scrimVisible = !!video.showPlayScrim && !tapped;

  usePlayWhileVisible(ref, true);

  const takeControl = () => {
    setTapped(true);
    const el = ref.current;
    if (el) {
      // they asked for this one rather than scrolled past it, so hand over the real controls
      el.controls = true;
      void el.play().catch(() => {});
    }
  };

  return (
    <picture
      className={classNames(styles.shell, className, {
        [styles.scrimmed]: video.showPlayScrim,
        [styles.fills]: fill,
      })}
    >
      <video
        ref={ref}
        className={styles.video}
        preload={video.preload ?? 'auto'}
        poster={video.poster}
        autoPlay={true}
        muted={true}
        loop={true}
        playsInline={true}
        src={video.sources ? undefined : video.url}
      >
        {video.sources?.map((source) => (
          <source key={source.src} src={source.src} type={source.type} />
        ))}
      </video>
      {scrimVisible && (
        <button className={styles.scrim} onClick={takeControl} type="button" aria-label="Play video">
          <span className={styles.play}>
            <PlayIcon />
          </span>
        </button>
      )}
    </picture>
  );
};
